/**
 * Beckham Stack Delay Popup
 * Intercepts add-to-cart on Beckham Stack Pro product pages AND in the HB popup.
 * Shows a delivery delay notification and only proceeds after user confirmation.
 */

const BSD_HANDLES = ['the-beckham-stack-pro', 'beckhamstack', 'beckham-stack'];

const BSD_OVERLAY_HTML = `<div id="bsd-delay-overlay" class="bsd-overlay">
  <div class="bsd-overlay__backdrop"></div>
  <div class="bsd-popup">
    <button class="bsd-popup__close" aria-label="Close" data-bsd-close>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
      </svg>
    </button>
    <div class="bsd-popup__badge">
      <span class="bsd-popup__badge-dot"></span>
      High Demand
    </div>
    <h2 class="bsd-popup__title">Delivery Delayed by 4&ndash;6 Weeks</h2>
    <p class="bsd-popup__desc">
      Due to exceptionally high demand for <strong>The Beckham Stack</strong>,
      current orders are shipping within <strong>4 to 6 weeks</strong> instead
      of our usual timeframe. Your order will be prioritized and shipped as
      soon as stock is replenished.
    </p>
    <div class="bsd-popup__timeline">
      <div class="bsd-popup__timeline-step">
        <div class="bsd-popup__timeline-icon bsd-popup__timeline-icon--order">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M3.5 9.5L7 13L14.5 5.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <span class="bsd-popup__timeline-label">Order</span>
        <span class="bsd-popup__timeline-sub">Today</span>
      </div>
      <div class="bsd-popup__timeline-connector">
        <span class="bsd-popup__timeline-connector-line"></span>
      </div>
      <div class="bsd-popup__timeline-step">
        <div class="bsd-popup__timeline-icon bsd-popup__timeline-icon--prep">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M9 4.5V9L12 11.25" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="9" cy="9" r="6.5" stroke="currentColor" stroke-width="1.6"/>
          </svg>
        </div>
        <span class="bsd-popup__timeline-label">Preparation</span>
        <span class="bsd-popup__timeline-sub">4&ndash;6 weeks</span>
      </div>
      <div class="bsd-popup__timeline-connector">
        <span class="bsd-popup__timeline-connector-line"></span>
      </div>
      <div class="bsd-popup__timeline-step">
        <div class="bsd-popup__timeline-icon bsd-popup__timeline-icon--deliver">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M2.25 5.25L9 2.25L15.75 5.25V12.75L9 15.75L2.25 12.75V5.25Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
            <path d="M9 8.25V15.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            <path d="M2.25 5.25L9 8.25L15.75 5.25" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
          </svg>
        </div>
        <span class="bsd-popup__timeline-label">Delivery</span>
        <span class="bsd-popup__timeline-sub">3&ndash;5 days</span>
      </div>
    </div>
    <button class="bsd-popup__confirm" data-bsd-confirm>
      <span class="loading__spinner hidden"></span>
      <span class="bsd-popup__confirm-text">I Understand &mdash; Add to Cart</span>
    </button>
    <button class="bsd-popup__cancel" data-bsd-cancel>Go back</button>
    <p class="bsd-popup__thanks">Thank you for your patience. We appreciate your support.</p>
  </div>
</div>`;

class BeckhamStackDelayPopup {
  constructor() {
    this.overlay = document.getElementById('bsd-delay-overlay');
    this.popup = this.overlay ? this.overlay.querySelector('.bsd-popup') : null;
    this.pendingFormSubmit = null;
    this.pendingProductForm = null;
    this.pendingOtpLink = null;
    this.interceptedForms = new WeakSet();
    this.cssLoaded = !!document.querySelector('link[href*="beckham-stack-delay-popup"]');

    if (this.overlay) {
      this.bindEvents();
    }

    this.interceptProductForms();
    this.observeDynamicForms();
  }

  isBeckhamStackForm(productForm) {
    const productInfo = productForm.closest('product-info');
    if (!productInfo) return false;
    const handle = productInfo.getAttribute('data-product-handle');
    if (handle) return BSD_HANDLES.includes(handle);

    const url = productInfo.getAttribute('data-url') || '';
    return BSD_HANDLES.some(h => url.includes('/products/' + h));
  }

  ensureOverlay() {
    if (this.overlay) return true;

    if (!this.cssLoaded) {
      const cssUrl = window.bsdDelayCssUrl;
      if (cssUrl) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssUrl;
        document.head.appendChild(link);
      }
      this.cssLoaded = true;
    }

    const temp = document.createElement('div');
    temp.innerHTML = BSD_OVERLAY_HTML;
    this.overlay = temp.firstElementChild;
    document.body.appendChild(this.overlay);
    this.popup = this.overlay.querySelector('.bsd-popup');
    this.bindEvents();
    return true;
  }

  interceptProductForms() {
    document.querySelectorAll('product-form').forEach(pf => this.interceptSingleForm(pf));
    this.interceptStickyButton();
    this.interceptOneTimePurchase();
  }

  interceptSingleForm(productForm) {
    const form = productForm.querySelector('form');
    if (!form || this.interceptedForms.has(form)) return;
    this.interceptedForms.add(form);

    form.addEventListener('submit', (evt) => {
      if (this._confirming) return;

      const isOnBeckhamPage = window.bsdDelayPopupEnabled;
      const isHbBeckham = this.isBeckhamStackForm(productForm);
      if (!isOnBeckhamPage && !isHbBeckham) return;

      evt.preventDefault();
      evt.stopImmediatePropagation();

      this.pendingProductForm = productForm;
      this.pendingFormSubmit = form;

      this.ensureOverlay();
      this.open();
    }, true);
  }

  observeDynamicForms() {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          if (node.matches && node.matches('product-form')) {
            this.interceptSingleForm(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('product-form').forEach(pf => this.interceptSingleForm(pf));
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  interceptStickyButton() {
    const stickyBtns = document.querySelectorAll('.sticky-addtocart-button .button, .sticky_addtocart_right_new .button');
    stickyBtns.forEach(btn => {
      if (btn.getAttribute('href') === '#product' || btn.getAttribute('href')?.startsWith('#')) return;

      btn.addEventListener('click', (evt) => {
        if (!window.bsdDelayPopupEnabled) return;
        evt.preventDefault();
        evt.stopImmediatePropagation();

        const mainForm = document.querySelector('product-form');
        if (mainForm) {
          this.pendingProductForm = mainForm;
          this.pendingFormSubmit = mainForm.querySelector('form');
        }
        this.ensureOverlay();
        this.open();
      }, true);
    });
  }

  interceptOneTimePurchase() {
    document.addEventListener('click', (evt) => {
      if (this._confirming) return;

      const otpLink = evt.target.closest('.one-time-purchase-link');
      if (!otpLink) return;

      const productInfo = otpLink.closest('product-info');
      const isOnBeckhamPage = window.bsdDelayPopupEnabled;
      let isHbBeckham = false;
      if (productInfo) {
        const handle = productInfo.getAttribute('data-product-handle');
        if (handle) isHbBeckham = BSD_HANDLES.includes(handle);
        const url = productInfo.getAttribute('data-url') || '';
        if (!isHbBeckham) isHbBeckham = BSD_HANDLES.some(h => url.includes('/products/' + h));
      }

      if (!isOnBeckhamPage && !isHbBeckham) return;

      evt.preventDefault();
      evt.stopImmediatePropagation();

      this.pendingOtpLink = otpLink;
      this.pendingProductForm = null;
      this.pendingFormSubmit = null;

      this.ensureOverlay();
      this.open();
    }, true);
  }

  bindEvents() {
    if (!this.overlay) return;

    const closeBtn = this.overlay.querySelector('[data-bsd-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    const cancelBtn = this.overlay.querySelector('[data-bsd-cancel]');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => this.close());
    }

    const backdrop = this.overlay.querySelector('.bsd-overlay__backdrop');
    if (backdrop) {
      backdrop.addEventListener('click', () => this.close());
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    const confirmBtn = this.overlay.querySelector('[data-bsd-confirm]');
    if (confirmBtn) {
      confirmBtn.addEventListener('click', () => this.confirmAndSubmit());
    }
  }

  open() {
    if (!this.overlay) return;
    document.body.style.overflow = 'hidden';
    this.overlay.classList.add('bsd-overlay--open');
  }

  close() {
    if (!this.overlay) return;
    this.overlay.classList.remove('bsd-overlay--open');
    document.body.style.overflow = '';

    // Reset any loading/disabled state on the form's submit button
    if (this.pendingProductForm) {
      const btn = this.pendingProductForm.querySelector('[type="submit"]');
      if (btn) {
        btn.classList.remove('loading');
        btn.removeAttribute('aria-disabled');
      }
      const spinner = this.pendingProductForm.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
    }

    // Reset the sticky buy button's disabled state (it's outside product-form)
    const stickyBtn = document.querySelector('.product-buy-sticky__button');
    if (stickyBtn) {
      stickyBtn.classList.remove('disabled');
    }

    this.pendingFormSubmit = null;
    this.pendingProductForm = null;
    this.pendingOtpLink = null;
  }

  isOpen() {
    return this.overlay && this.overlay.classList.contains('bsd-overlay--open');
  }

  confirmAndSubmit() {
    const confirmBtn = this.overlay.querySelector('[data-bsd-confirm]');

    if (confirmBtn) {
      confirmBtn.classList.add('loading');
      const spinner = confirmBtn.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
    }

    // Temporarily disable so re-submitted form goes through
    const wasBsdEnabled = window.bsdDelayPopupEnabled;
    window.bsdDelayPopupEnabled = false;

    // Also set a flag the interceptSingleForm handler can check
    this._confirming = true;

    this.overlay.classList.remove('bsd-overlay--open');
    document.body.style.overflow = '';

    if (this.pendingOtpLink) {
      this.pendingOtpLink.click();
    } else if (this.pendingProductForm) {
      const submitBtn = this.pendingProductForm.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.click();
      }
    }

    setTimeout(() => {
      window.bsdDelayPopupEnabled = wasBsdEnabled;
      this._confirming = false;

      if (confirmBtn) {
        confirmBtn.classList.remove('loading');
        const spinner = confirmBtn.querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');
      }
    }, 2000);

    this.pendingFormSubmit = null;
    this.pendingProductForm = null;
    this.pendingOtpLink = null;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.bsdDelayPopup = new BeckhamStackDelayPopup();
});
